// Sugalarm — kiosk native volume bridge (cycle 317).
//
// The single owner of access to the native `KioskVolume` plugin. That plugin
// exists ONLY in the Android kiosk APK (the `kiosk` product flavor); it is
// absent on web, the Pi kiosk, iPhone, the phone layout, and the `standard`
// Android flavor — so `isAvailable()` is false on every surface except the
// tablet kiosk APK, and the header volume button (native path) gates on it.
//
// Reached through the bridge-injected `window.Capacitor.Plugins` proxy —
// NEVER a bare `import("@capacitor/...")` (cycle 26/138/197). Mirrors
// `lib/battery.ts` / `lib/screenBrightness.ts`.
//
// Drives STREAM_MUSIC — the stream the in-app WebView alarm audio + TTS play
// on. The native `setVolume` floors the level so the in-app slider can never
// silence a glucose-alarm kiosk; `getVolume` reports the true current level.

import { isCapacitorNative } from "./capacitor";

export interface KioskVolumeState {
  /** 0-100, or -1 when genuinely unreadable. */
  level: number;
  muted: boolean;
  supported?: boolean;
}

interface KioskVolumeProxy {
  getVolume(): Promise<KioskVolumeState>;
  setVolume(opts: { level: number }): Promise<KioskVolumeState>;
}

/** Resolve the natively-registered plugin proxy off the Capacitor bridge, or
 *  null on any surface where it isn't present. */
function plugin(): KioskVolumeProxy | null {
  if (!isCapacitorNative()) return null;
  const cap = (
    window as { Capacitor?: { Plugins?: Record<string, unknown> } }
  ).Capacitor;
  const p = cap?.Plugins?.KioskVolume;
  return p != null ? (p as KioskVolumeProxy) : null;
}

/** True iff the native KioskVolume plugin is registered on the bridge — i.e.
 *  the SPA is running inside the Android tablet kiosk APK. The header's
 *  native volume control renders only when this is true. */
export function isAvailable(): boolean {
  return plugin() != null;
}

/** Read the current volume, or null on a non-kiosk-APK surface / bridge
 *  error. */
export async function getVolume(): Promise<KioskVolumeState | null> {
  const p = plugin();
  if (p == null) return null;
  try {
    return await p.getVolume();
  } catch (err) {
    console.warn("[kioskVolume] getVolume failed", err);
    return null;
  }
}

/** Set the volume (0-100; the native side floors it). Returns the resulting
 *  true state, or null on failure. */
export async function setVolume(level: number): Promise<KioskVolumeState | null> {
  const p = plugin();
  if (p == null) return null;
  try {
    return await p.setVolume({ level: Math.max(0, Math.min(100, Math.round(level))) });
  } catch (err) {
    console.warn("[kioskVolume] setVolume failed", err);
    return null;
  }
}
